import React from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconCircleDot } from '@tabler/icons-react';
import { HeavyDutyMachineryOption } from '../constants/heavyDutyMachinerySpaceOptions';
import { HEAVY_DUTY_MACHINERY_OPTION_ICONS } from '../constants/heavyDutyMachineryOptionIcons';
import { translate } from '../helpers/translationHelper';

interface WizardTileGridProps {
  options: HeavyDutyMachineryOption[];
  // Optional here even though this is always used inside a Form.Item, which
  // injects both at clone time -- TypeScript only sees the JSX as written,
  // where neither is passed explicitly (same convention as antd's own
  // Select/Checkbox.Group typings).
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  /** Checkbox-like (many can be on) instead of radio-like (exactly one). */
  multiple?: boolean;
  disabled?: boolean;
  minTileWidth?: number;
}

/**
 * Big-icon tile grid for single/multi-select fields in the publish wizard --
 * same visual language as FiltersModal's own tile mode (icon circle + label,
 * selected = filled circle + tinted border), just standalone (no facet
 * counts) and reused for both antd Select and Checkbox.Group replacements.
 */
const WizardTileGrid: React.FC<WizardTileGridProps> = ({ options, value, onChange, multiple, disabled, minTileWidth = 130 }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const selectedValues = multiple ? ((value as string[]) ?? []) : value ? [value as string] : [];

  const toggle = (optionValue: string) => {
    if (disabled) return;
    if (multiple) {
      const current = (value as string[]) ?? [];
      onChange?.(current.includes(optionValue) ? current.filter((v) => v !== optionValue) : [...current, optionValue]);
    } else {
      onChange?.(optionValue);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minTileWidth}px, 1fr))`, gap: 12 }}>
      {options.map((option) => {
        const Icon = HEAVY_DUTY_MACHINERY_OPTION_ICONS[option.value] ?? IconCircleDot;
        const selected = selectedValues.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            className="wizard-tile"
            data-value={option.value}
            onClick={() => toggle(option.value)}
            disabled={disabled}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: '18px 12px',
              borderRadius: 18,
              border: `1.5px solid ${selected ? token.colorPrimary : token.colorBorderSecondary}`,
              // Derived from colorPrimary via antd's own token algorithm, not a
              // hardcoded tint, so the fill follows whatever accent this tree is
              // rendering with -- the brand coral by default, teal inside a
              // booking subtree.
              background: selected ? token.colorPrimaryBg : token.colorBgContainer,
              cursor: disabled ? 'default' : 'pointer',
              textAlign: 'center',
              transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: selected ? token.colorPrimary : token.colorFillTertiary,
                flexShrink: 0,
              }}
            >
              <Icon size={22} color={selected ? token.colorWhite ?? '#fff' : token.colorTextSecondary} />
            </div>
            <span style={{ fontSize: 13, lineHeight: 1.3 }}>{translate(t, option.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
};

export default WizardTileGrid;
